/**
 * detailCache — module-level cache for detail pages (ActivityDetail, PlaceDetail,
 * Venue). These pages are opened repeatedly from the feed (open A → back → open B
 * → back → reopen A), and each previously refetched its whole detail payload on
 * every mount. This caches the fetched result per id so repeat-opens render
 * instantly, with a background revalidation to keep counts/flags fresh.
 *
 * Mirrors the dashboardCache / feedCache / savedItemsCache pattern. Generic
 * key→value: each page stores and restores its own result shape verbatim, so the
 * module stays agnostic to what a given page's payload looks like.
 *
 * Keys are namespaced by type to avoid collisions across pages, and scoped by
 * viewer id — cached payloads carry viewer-specific fields (user_liked,
 * userSaved, is_following, friend-gated topTrips), so user A's entry must
 * never be served to user B after an account switch. Anonymous viewers share
 * the 'anon' scope. E.g. `activity:123:u-abc`, `place:ChIJ...:anon`.
 */

const detailCache = new Map();
const DETAIL_TTL_MS = 60_000;

export const buildDetailKey = (type, id, userId) => `${type}:${id}:${userId || 'anon'}`;

// Returns { data, isStale } when an entry exists, else null. A stale entry is
// kept (not evicted) so the caller can render it immediately and revalidate in
// the background — same stale-while-revalidate shape as the home dashboard.
export const getCachedDetail = (key) => {
  if (!key) return null;
  const entry = detailCache.get(key);
  if (!entry) return null;
  return { data: entry.data, isStale: Date.now() - entry.ts > DETAIL_TTL_MS };
};

export const setCachedDetail = (key, data) => {
  if (!key) return;
  detailCache.set(key, { data, ts: Date.now() });
};

// Drop a single entry (e.g. after a mutation that materially changes the detail),
// or clear everything when called with no key (sign-out).
export const invalidateDetailCache = (key) => {
  if (!key) {
    detailCache.clear();
    return;
  }
  detailCache.delete(key);
};

// Drop every entry of one page type — every viewer, every id. For pages whose
// cached payload carries like/save state that a mutation ELSEWHERE can change:
// one real place is reachable at /place/atr_… and /place/ChIJ…, and the server
// reports its hearts across both, so a like on either (or on a feed card)
// leaves the other URL's entry stale; a destination page's sections carry the
// same state for every card on it. The caller cannot know which entries hold
// the thing that changed, so all entries of the type go; each refetches once
// on its next open.
export const invalidateDetailCacheType = (type) => {
  if (!type) return;
  const prefix = `${type}:`;
  for (const key of detailCache.keys()) {
    if (key.startsWith(prefix)) detailCache.delete(key);
  }
};

export { DETAIL_TTL_MS };
